import './MeasurementDetail.scss';
// The shared Sessions components (pitch / goal charts, kick info card, session previews, tiles) are styled by Sessions.scss, which
// nothing else imports: load it here, after the page's own styles, so its rules sit where they always did in the cascade.
import 'pages/Sessions/Sessions.scss';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useLocation, useHistory } from 'react-router-dom';
import { AppSectionErrorBoundary, ChartCard, LoadingOverlay } from 'components/ui';
import { useStores } from 'index';
import { t } from 'utilities/TranslationUtils';
import { IResult } from 'services/Result';
import { AxiosResponseHelper } from 'utilities/AxiosResponseHelper';
import {
  SessionDetailsResponseDto,
  MeasurementDetailDto,
} from 'types/models/MeasurementDetail';
import {
  PlayerInfo,
  PlayerGoalFaceData,
} from 'types/models/OrgSnapshotData';

import GoalFrontViewChart from '../Players/charts/GoalFrontViewChart';
import BallTrajectory3DChart, { BallTrajectoryData, OtherTrajectory } from '../Players/charts/BallTrajectory3DChart';
import { SessionEnrichment } from 'types/models/SessionEnrichment';
import { soccerGoalCrossingToChart } from './coords';
import { measurementDetailUrl, playerPerformanceUrl, sessionPlayerUrl } from 'routePaths';
import { KIND_FILTER } from 'pages/PlayerPerformance/playerPerformanceUtils';
import { SessionKindGroupName } from 'types/models/SessionsPageData';
import dayjs from 'dayjs';
import { formatSessionDateTime } from 'utilities/DateUtils';
import { kickMarkerColor } from 'utilities/chartUtils';
import { buildZip, MAX_ZIP_BYTES } from 'utilities/zip';
import VideoModal from './VideoModal';
import SessionTopView from './SessionTopView';
import SessionNeighbourLinks from './SessionNeighbourLinks';
import { countTitle } from './kickWording';
import { prefersTopView } from 'types/models/SessionsPageData';
import SessionPlayerPicker, { SessionPlayer } from './SessionPlayerPicker';
import PullDirectionCard from '../Sessions/components/PullDirectionCard';
import KickResultChip from '../Sessions/components/KickResultChip';

const SINGLE_COLOR = ['#4C8EF7'];

/**
 * Adapts the BFF DTO into the shape the existing 2D charts expect — they were built for
 * multi-player aggregate views, so we wrap the focused measurement in a one-element
 * "player" array. The 3D chart already takes a single trajectory natively.
 */
type TrajectoryMatch = { d2: number; y: number | null };

const buildReachableCandidates = (coords: [number, number]): [number, number][] => {
  const a = coords[0];
  const b = coords[1];
  return [
    [a, b], [a, -b], [-a, b], [-a, -b],
    [b, a], [b, -a], [-b, a], [-b, -a],
  ];
};

const evalPolyLocal = (coeffs: number[] | undefined, t: number): number => {
  if (!coeffs || coeffs.length === 0) return 0;
  let acc = 0;
  for (let i = 0; i < coeffs.length; i++) acc += coeffs[i] * (t ** i);
  return acc;
};

const matchToTrajectory = (trajectory: any, targetX: number, targetZ: number): TrajectoryMatch | null => {
  if (!trajectory?.segments?.length) return null;
  let bestY: number | null = null;
  let bestD2 = Number.POSITIVE_INFINITY;
  for (const segment of trajectory.segments) {
    const [t0, t1] = segment.timeInterval;
    const span = Math.max(0, t1 - t0);
    const steps = Math.max(10, Math.ceil(span * 40));
    for (let i = 0; i <= steps; i++) {
      const u = i / steps;
      const t = t0 + span * u;
      const x = evalPolyLocal(segment.x, t);
      const z = evalPolyLocal(segment.z, t);
      const y = evalPolyLocal(segment.y, t);
      const d2 = (x - targetX) ** 2 + (z - targetZ) ** 2;
      if (d2 < bestD2) {
        bestD2 = d2;
        bestY = y;
      }
    }
  }
  return Number.isFinite(bestD2) ? { d2: bestD2, y: bestY } : null;
};

const resolveReachableSegment = (
  startRaw: [number, number],
  endRaw: [number, number],
  measurement: { kickX: number | null; kickZ: number | null },
  trajectory: any,
): { start: [number, number]; end: [number, number]; startY: number | null; endY: number | null } | null => {
  const startCandidates = buildReachableCandidates(startRaw);
  const endCandidates = buildReachableCandidates(endRaw);
  const count = Math.min(startCandidates.length, endCandidates.length);
  if (count === 0) return null;
  let best: { start: [number, number]; end: [number, number]; startY: number | null; endY: number | null; score: number } | null = null;
  for (let i = 0; i < count; i++) {
    const start = startCandidates[i];
    const end = endCandidates[i];
    const startMatch = matchToTrajectory(trajectory, start[0], start[1]);
    const endMatch = matchToTrajectory(trajectory, end[0], end[1]);
    let score = Number.POSITIVE_INFINITY;
    if (startMatch && endMatch) {
      score = startMatch.d2 + endMatch.d2;
    } else if (measurement.kickX !== null && measurement.kickZ !== null) {
      score = (start[0] - measurement.kickX) ** 2 + (start[1] - measurement.kickZ) ** 2 + (end[0] - measurement.kickX) ** 2 + (end[1] - measurement.kickZ) ** 2;
    }
    if (!best || score < best.score) {
      best = {
        start,
        end,
        startY: startMatch?.y ?? null,
        endY: endMatch?.y ?? null,
        score,
      };
    }
  }
  if (!best) return null;
  return { start: best.start, end: best.end, startY: best.startY, endY: best.endY };
};

/**
 * Adapts the BFF DTO into the shape the existing 2D charts expect — they were built for
 * multi-player aggregate views, so we wrap the focused measurement in a one-element
 * "player" array. The 3D chart already takes a single trajectory natively.
 */
/** The focused player's kicks (every kick when the focused one has no player id), in the BFF's time order. */
function playerKicksOf(details: SessionDetailsResponseDto): MeasurementDetailDto[] {
  const playerId = details.measurement.playerId;
  return playerId ? details.measurements.filter((mm) => mm.playerId === playerId) : details.measurements;
}

function buildChartAdapters(details: SessionDetailsResponseDto, playerKicks: MeasurementDetailDto[]) {
  const m = details.measurement;
  // Colour of a kick = its result colour everywhere (chip, markers, 3D tube); binary kinds follow their on-target score.
  const resultColor = (k: MeasurementDetailDto): string =>
    kickMarkerColor({ result: k.kickResult, kind: details.kind, onTarget: k.onTarget }, details.kind);
  const playerId = m.playerId ?? m.measurementId;
  const player: PlayerInfo = {
    playerId,
    playerName: m.playerName || t('measurementDetail.unknownPlayer'),
    teamName: '',
    measurementCount: 1,
    measurementCountBySessionType: { [details.sessionType]: 1 },
  };

  // The goal view and 3D/top view show all of the player's kicks, with the focused kick highlighted.
  // Note: Z is flipped for the 2D charts to match the 3D view's side convention.
  // See ./coords.ts for the rationale. Goal crossing may be missing (e.g. the ball never reached the
  // goal plane) — such kicks are simply left out of the goal view.
  const goalFace: PlayerGoalFaceData = {
    playerId,
    entries: playerKicks
      .filter((k) => k.trajectory.goalCrossing)
      .map((k) => {
        const gcChart = soccerGoalCrossingToChart(k.trajectory.goalCrossing!);
        return {
          y: gcChart.y,
          z: gcChart.z,
          kickResult: k.kickResult,
          sessionType: details.sessionType,
          kickId: k.measurementId,
          sessionId: details.sessionId,
          dateTime: k.capturedAtUtc,
          ballSpeed: k.ballSpeed ?? null,
          hangTime: k.hangTime ?? null,
        };
      }),
  };

  const trajectory3d: BallTrajectoryData = {
    segments: m.trajectory.segments.map((s) => ({
      timeInterval: s.timeInterval,
      x: s.x,
      y: s.y,
      z: s.z,
    })),
    kickLocation: m.trajectory.kickLocation,
    goalCrossing: m.trajectory.goalCrossing,
    color: resultColor(m),
  };

  // Other session trajectories — everything except the focused measurement, used for the
  // "show all session trajectories" overlay in the 3D chart.
  const otherTrajectories: OtherTrajectory[] = playerKicks
    .filter((s) => s.measurementId !== m.measurementId && s.trajectory.segments.length > 0)
    .map((s) => ({
      id: s.measurementId,
      kickResult: s.kickResult,
      color: resultColor(s),
      sessionId: details.sessionId,
      data: {
        segments: s.trajectory.segments.map((seg) => ({
          timeInterval: seg.timeInterval,
          x: seg.x,
          y: seg.y,
          z: seg.z,
        })),
        kickLocation: s.trajectory.kickLocation,
        goalCrossing: s.trajectory.goalCrossing,
      },
    }));

  return { player, goalFace, trajectory3d, otherTrajectories };
}

function formatMetric(value: number | null | undefined, unit: string, digits = 1): string {
  if (value === null || value === undefined) return '—';
  return `${value.toFixed(digits)} ${unit}`;
}

const SUMMARY_METRICS: ReadonlyArray<{
  labelKey: string;
  get: (m: { accuracyPct?: number | null; ballSpeed?: number | null; spinRate?: number | null; launchAngle?: number | null; maxHeight?: number | null; hangTime?: number | null; gFactor?: number | null }) => number | null | undefined;
  unit: string;
  digits?: number;
}> = [
  { labelKey: 'measurementDetail.accuracy',   get: (m) => m.accuracyPct, unit: '%', digits: 0 },
  { labelKey: 'measurementDetail.ballSpeed',  get: (m) => m.ballSpeed,   unit: 'km/h' },
  { labelKey: 'measurementDetail.spinRate',   get: (m) => m.spinRate,    unit: 'rpm', digits: 0 },
  { labelKey: 'measurementDetail.launchAngle',get: (m) => m.launchAngle, unit: '°' },
  { labelKey: 'measurementDetail.maxHeight',  get: (m) => m.maxHeight,   unit: 'm',   digits: 2 },
  { labelKey: 'measurementDetail.hangTime',   get: (m) => m.hangTime,    unit: 's',   digits: 2 },
  { labelKey: 'measurementDetail.gFactor',    get: (m) => m.gFactor,     unit: 'g' },
];

// Session-scoped cache: the BFF returns full detail for every measurement, so we fetch a session
// once and switch the focused kick entirely client-side. Enrichment (wall + targets) is per-kick
// and cached separately. Both survive across in-page navigations within the SPA session.
const sessionCache = new Map<string, SessionDetailsResponseDto>();
const enrichmentCache = new Map<string, SessionEnrichment | null>();

/** Returns a details view whose focused `measurement` is the requested kick, selected client-side. */
function focusSession(session: SessionDetailsResponseDto, measurementId: string): SessionDetailsResponseDto {
  const focused = session.measurements.find((mm) => mm.measurementId === measurementId);
  return focused ? { ...session, measurement: focused } : session;
}

const MeasurementDetailPage: React.FunctionComponent = observer(() => {
  const { apiServiceAxiosInstance: api } = useStores();
  const location = useLocation();
  const history = useHistory();

  const params = new URLSearchParams(location.search);
  const sessionId = params.get('sessionId') ?? '';
  const measurementId = params.get('measurementId') ?? '';
  // Session-card entry: no kick in the URL yet — the BFF focuses this player's first kick.
  const entryPlayerId = params.get('playerId') ?? '';

  const [session, setSession] = useState<SessionDetailsResponseDto | null>(() => sessionCache.get(sessionId) ?? null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  // SharedCalculator enrichment (wall + targets) for the 3D view — loaded best-effort, never blocks the page.
  const [enrichment, setEnrichment] = useState<SessionEnrichment | null>(() => enrichmentCache.get(`${sessionId}:${measurementId}`) ?? null);

  // Fetch the whole session ONCE per sessionId. Switching the focused kick only changes the URL's
  // measurementId, which is resolved client-side below — no refetch.
  useEffect(() => {
    if (!sessionId || (!measurementId && !entryPlayerId)) {
      setLoadError(t('measurementDetail.missingParams'));
      return;
    }
    const cached = sessionCache.get(sessionId);
    if (cached) {
      setSession(cached);
      setLoadError(null);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setLoadError(null);
    api
      .get<IResult<SessionDetailsResponseDto>>(
        `api/SessionDetails?sessionId=${encodeURIComponent(sessionId)}${
          measurementId ? `&measurementId=${encodeURIComponent(measurementId)}` : `&playerId=${encodeURIComponent(entryPlayerId)}`
        }`,
      )
      .then((response) => {
        if (cancelled) return;
        if (AxiosResponseHelper.IsSuccessfulResponse(response) && response.data?.data) {
          sessionCache.set(sessionId, response.data.data);
          setSession(response.data.data);
        } else {
          setLoadError(t('measurementDetail.loadFailed'));
        }
      })
      .catch(() => {
        if (cancelled) return;
        setLoadError(t('measurementDetail.loadFailed'));
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // measurementId intentionally omitted: switching kicks must NOT refetch the session.
  }, [sessionId, api]);

  // Keep the URL canonical: always carry the focused kick AND its player. Entered with only a player
  // (session cards, player picker) → pick that player's earliest kick here, since a cached session may
  // have been focused on someone else.
  useEffect(() => {
    if (!session || session.sessionId !== sessionId) return;
    const target = measurementId
      ? session.measurements.find((mm) => mm.measurementId === measurementId)
      : session.measurements.find((mm) => mm.playerId === entryPlayerId) ?? session.measurement;
    if (!target) return;
    if (target.measurementId === measurementId && (target.playerId ?? '') === entryPlayerId) return;
    history.replace(measurementDetailUrl(sessionId, target.measurementId, target.playerId));
  }, [measurementId, entryPlayerId, session, sessionId, history]);

  // Kick enrichment (wall + targets) is an optional per-kick enhancement for the 3D view. Cached per
  // measurement so revisiting a kick is instant; never blocks the page.
  useEffect(() => {
    if (!sessionId || !measurementId) return;
    const key = `${sessionId}:${measurementId}`;
    if (enrichmentCache.has(key)) {
      setEnrichment(enrichmentCache.get(key) ?? null);
      return;
    }
    let cancelled = false;
    setEnrichment(null);
    api
      .get<IResult<SessionEnrichment>>(
        `api/SessionEnrichment?sessionId=${encodeURIComponent(sessionId)}&measurementId=${encodeURIComponent(measurementId)}`,
      )
      .then((response) => {
        if (cancelled) return;
        const data = AxiosResponseHelper.IsSuccessfulResponse(response) && response.data?.data ? response.data.data : null;
        enrichmentCache.set(key, data);
        setEnrichment(data);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [sessionId, measurementId, api]);

  const details = session ? focusSession(session, measurementId) : null;
  const measurementMissing = !!session && !!measurementId && !session.measurements.some((mm) => mm.measurementId === measurementId);

  return (
    <div className='measurement-detail-page'>
      {isLoading && !session && (
        <div className='measurement-detail-page__loader'>
          <LoadingOverlay isVisible />
        </div>
      )}

      {loadError && !isLoading && (
        <div className='measurement-detail-page__error'>
          <p>{loadError}</p>
        </div>
      )}

      {measurementMissing && !isLoading && !loadError && (
        <div className='measurement-detail-page__error'>
          <p>{t('measurementDetail.loadFailed')}</p>
        </div>
      )}

      {details && !measurementMissing && !loadError && (
        <MeasurementDetailContent details={details} enrichment={enrichment} />
      )}
    </div>
  );
});

/** Distinct players in the session, in order of their first kick. */
function sessionPlayersOf(measurements: MeasurementDetailDto[]): SessionPlayer[] {
  const players = new Map<string, SessionPlayer>();
  for (const mm of measurements) {
    if (mm.playerId && !players.has(mm.playerId)) {
      players.set(mm.playerId, { id: mm.playerId, name: mm.playerName, avatarUrl: mm.playerAvatarUrl });
    }
  }
  return Array.from(players.values());
}

const MeasurementDetailContent = ({ details, enrichment }: { details: SessionDetailsResponseDto; enrichment: SessionEnrichment | null }) => {
  const history = useHistory();
  const m = details.measurement;
  const playerKicks = useMemo(() => playerKicksOf(details), [details]);
  const { player, goalFace, trajectory3d, otherTrajectories } = useMemo(() => buildChartAdapters(details, playerKicks), [details, playerKicks]);
  const isThrow = details.kind === 'ThrowIn';
  const usesTopView = prefersTopView(details.kind);
  // Chips need the kind GROUP (Corner / Goalkeeper …), not the raw session type, to apply the binary on-target rules.
  const chipKind = details.kind || details.sessionType;
  const kickNumbers = useMemo(() => buildKickNumbers(playerKicks), [playerKicks]);
  const [viewTab, setViewTab] = useState<'goal' | '3d'>('goal');
  const [openVideoIndex, setOpenVideoIndex] = useState<number | null>(null);
  // Navigating to another kick closes the lightbox (its videos belong to the previous kick).
  useEffect(() => { setOpenVideoIndex(null); }, [m.measurementId]);
  const summary = details.playerSummaries?.find((x) => x.playerId === m.playerId);
  const sessionPlayers = useMemo(() => sessionPlayersOf(details.measurements), [details.measurements]);
  // Switching kick within the session: same data, only the URL changes (the page effect keeps it canonical).
  const selectKick = useCallback(
    (kickId: string) => history.push(measurementDetailUrl(details.sessionId, kickId, m.playerId)),
    [history, details.sessionId, m.playerId],
  );

  const reachableRegionSegments: Array<{
    id: string;
    sessionId: string;
    start: [number, number];
    end: [number, number];
    startY: number | null;
    endY: number | null;
    startTime?: number | null;
    endTime?: number | null;
    onTarget: number | null;
    isActive: boolean;
  }> = [];
  if (m.reachableRegionStart && m.reachableRegionEnd) {
    const resolved = resolveReachableSegment(
      m.reachableRegionStart,
      m.reachableRegionEnd,
      { kickX: m.kickX ?? null, kickZ: m.kickZ ?? null },
      m.trajectory,
    );
    if (resolved) {
      reachableRegionSegments.push({
        id: m.measurementId,
        sessionId: details.sessionId,
        start: resolved.start,
        end: resolved.end,
        startY: resolved.startY,
        endY: resolved.endY,
        startTime: m.reachableRegionStartTime ?? null,
        endTime: m.reachableRegionEndTime ?? null,
        onTarget: m.onTarget ?? null,
        isActive: true,
      });
    }
  }
  for (const other of playerKicks) {
    if (other.measurementId === m.measurementId) continue;
    if (!other.reachableRegionStart || !other.reachableRegionEnd) continue;
    const resolved = resolveReachableSegment(
      other.reachableRegionStart,
      other.reachableRegionEnd,
      { kickX: other.kickX ?? null, kickZ: other.kickZ ?? null },
      other.trajectory,
    );
    if (resolved) {
      reachableRegionSegments.push({
        id: other.measurementId,
        sessionId: details.sessionId,
        start: resolved.start,
        end: resolved.end,
        startY: resolved.startY,
        endY: resolved.endY,
        startTime: other.reachableRegionStartTime ?? null,
        endTime: other.reachableRegionEndTime ?? null,
        onTarget: other.onTarget ?? null,
        isActive: false,
      });
    }
  }

  return (
    <>
      {/* ── Toolbar: player picker on the left, the session time and date in the middle, previous / next session on the right ── */}
      <div className='measurement-detail-toolbar'>
        <SessionPlayerPicker
          players={sessionPlayers}
          selectedId={m.playerId ?? null}
          onSelect={(playerId) => history.push(sessionPlayerUrl(details.sessionId, playerId))}
        />
        {m.playerId && (
          <button
            type='button'
            className='measurement-detail-performance-link'
            // Back to the player's performance for this session type.
            onClick={() => history.push(playerPerformanceUrl(m.playerId as string, KIND_FILTER[(details.kind ?? '') as SessionKindGroupName]?.filterValue))}
            data-test='md-see-player-performance'
          >
            {t('measurementDetail.seePlayerPerformance')}
          </button>
        )}
        <h1 className='measurement-detail-toolbar__title font-25 mb-0' data-test='md-title'>{formatSessionDateTime(details.addedAtUtc)}</h1>
        <SessionNeighbourLinks sessionId={details.sessionId} playerId={m.playerId ?? null} />
      </div>

      {/* ── Dashboard: kicks | (views over video) | metrics; stacks into one column on small screens ── */}
      <div className='measurement-detail-dashboard'>
        <div className='measurement-detail-dashboard__kicks'>
          <AppSectionErrorBoundary>
            <ChartCard
              title={countTitle(isThrow, playerKicks.length)}
              subtitle={t('measurementDetail.sessionMeasurementsSubtitle')}
            >
              <SessionMeasurementsList
                kicks={playerKicks}
                kickNumbers={kickNumbers}
                sessionId={details.sessionId}
                currentId={m.measurementId}
                sessionType={chipKind}
              />
            </ChartCard>
          </AppSectionErrorBoundary>

          <ChartCard title={t('measurementDetail.sessionSummary')}>
            {/* Session date sits under the title, the same muted line the selected-kick card uses for its time. */}
            <div className='measurement-detail-selected' data-test='measurement-detail-session-date'>
              <span className='measurement-detail-selected__time'>{dayjs(details.addedAtUtc).format('DD MMM YYYY')}</span>
            </div>
            <dl className='measurement-detail-metrics measurement-detail-summary-metrics' data-test='measurement-detail-summary'>
              {summary && (
                <>
                  <Metric label={t(isThrow ? 'measurementDetail.summaryThrows' : 'measurementDetail.summaryKicks')} value={String(summary.kickCount)} />
                  {summary.goalCount != null && <Metric label={t('measurementDetail.summaryGoals')} value={String(summary.goalCount)} />}
                  <Metric label={t('measurementDetail.accuracy')} value={formatMetric(summary.avgAccuracyPct, '%', 0)} />
                  <Metric label={t('measurementDetail.summaryAvgSpeed')} value={formatMetric(summary.avgBallSpeed, 'km/h')} />
                  <Metric label={t('measurementDetail.summaryMaxSpeed')} value={formatMetric(summary.maxBallSpeed, 'km/h')} />
                </>
              )}
            </dl>
          </ChartCard>
        </div>

        <div className='measurement-detail-dashboard__main'>
          <AppSectionErrorBoundary>
            <ChartCard
              title={t(viewTab === 'goal' ? (usesTopView ? 'measurementDetail.topView' : 'measurementDetail.goalView') : 'measurementDetail.threeDView')}
              subtitle={viewTab === 'goal' ? t(usesTopView ? 'measurementDetail.topViewSubtitle' : 'measurementDetail.goalViewSubtitle') : undefined}
              controls={(
                <div className='pp-tabs' role='tablist'>
                  {(['goal', '3d'] as const).map((tab) => (
                    <button
                      key={tab}
                      type='button'
                      role='tab'
                      aria-selected={viewTab === tab}
                      className={`pp-tab${viewTab === tab ? ' pp-tab--active' : ''}`}
                      onClick={() => setViewTab(tab)}
                      data-test={`measurement-detail-view-tab-${tab}`}
                    >
                      {t(tab === 'goal' ? (usesTopView ? 'measurementDetail.topTab' : 'measurementDetail.goalTab') : 'measurementDetail.threeDTab')}
                    </button>
                  ))}
                </div>
              )}
            >
              <div className={`measurement-detail-chart-frame measurement-detail-chart-frame--views${viewTab === 'goal' && usesTopView ? ' is-top' : ''}`}>
                {viewTab === 'goal' && usesTopView ? (
                  <SessionTopView details={details} kicks={playerKicks} currentId={m.measurementId} onSelectKick={selectKick} targets={enrichment?.targets} />
                ) : viewTab === 'goal' ? (
                  // Always render the goal frame (empty markers when this kick has no crossing) so
                  // switching kicks never adds/removes a section and shifts the page.
                  <GoalFrontViewChart
                    data={[goalFace]}
                    players={[player]}
                    colors={SINGLE_COLOR}
                    sessionTypeFilter={null}
                    currentMeasurementId={m.measurementId}
                    onSelectKick={selectKick}
                    alwaysShowFrame
                    fill
                  />
                ) : (
                  <BallTrajectory3DChart
                    trajectory={trajectory3d}
                    otherTrajectories={otherTrajectories}
                    reachableRegionSegments={reachableRegionSegments}
                    initialShowOthers
                    // Goalkeeper distribution is read from the keeper's position.
                    defaultCameraPreset={details.kind === 'Goalkeeper' ? 'goalkeeper' : 'kickLocation'}
                    wallPlayers={enrichment?.wallPlayers}
                    targets={enrichment?.targets}
                    onSelectOther={(_sessionId, kickId) => selectKick(kickId)}
                  />
                )}
              </div>
            </ChartCard>
          </AppSectionErrorBoundary>

          <AppSectionErrorBoundary>
            <ChartCard
              title={videoCardTitle(m.videos.length)}
              controls={m.videos.length > 0 ? <VideoDownloadAllButton videos={m.videos} m={m} /> : undefined}
            >
              <VideoStrip videos={m.videos} onOpen={setOpenVideoIndex} />
            </ChartCard>
          </AppSectionErrorBoundary>
        </div>

        <div className='measurement-detail-dashboard__metrics'>
          <ChartCard title={t(isThrow ? 'measurementDetail.selectedThrow' : 'measurementDetail.selectedKick')}>
            <div className='measurement-detail-selected' data-test='measurement-detail-selected'>
              <span className='measurement-detail-selected__time'>{dayjs(m.capturedAtUtc).format('DD MMM YYYY, HH:mm')}</span>
            </div>
            <dl className='measurement-detail-metrics'>
              <Metric
                label={t('measurementDetail.result')}
                value={<KickResultChip result={m.kickResult} kind={chipKind} onTarget={m.onTarget ?? null} />}
              />
              {SUMMARY_METRICS.map((def) => (
                <Metric key={def.labelKey} label={t(def.labelKey)} value={formatMetric(def.get(m), def.unit, def.digits)} />
              ))}
            </dl>
            <div className='measurement-detail-metrics__pull'>
              <PullDirectionCard pullDirection={m.pullDirection ?? null} />
            </div>
          </ChartCard>
        </div>
      </div>

      {openVideoIndex !== null && m.videos.length > 0 && (
        <VideoModal videos={m.videos} initialIndex={openVideoIndex} onClose={() => setOpenVideoIndex(null)} />
      )}
    </>
  );
};

const Metric = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className='measurement-detail-metric'>
    <dt>{label}</dt>
    <dd>{value}</dd>
  </div>
);

/** Click-to-copy id chip used in the page header to expose session/measurement GUIDs. */
/** Thumbnails only (compact, fixed height); tapping one opens the video modal on that angle. */
const VideoStrip = ({ videos, onOpen }: { videos: MeasurementDetailDto['videos']; onOpen: (index: number) => void }) => (
  <div className='measurement-detail-videos-strip'>
    {videos.length === 0 ? (
      <p className='chart-empty'>{t('measurementDetail.noVideos')}</p>
    ) : (
      videos.map((v, i) => (
        <button
          key={v.videoId}
          type='button'
          className='measurement-detail-thumb'
          onClick={() => onOpen(i)}
          title={t('measurementDetail.videoPlay', { label: v.label ?? v.videoId })}
          data-test='measurement-detail-thumb'
        >
          {v.thumbnailUrl ? <img src={v.thumbnailUrl} alt='' loading='lazy' /> : <span className='measurement-detail-thumb__placeholder' />}
          <span className='measurement-detail-thumb__play' aria-hidden='true' />
          <span className='measurement-detail-thumb__label'>{v.label ?? v.videoId}</span>
        </button>
      ))
    )}
  </div>
);

/** Card title that reflects the number of attached camera angles. */
function videoCardTitle(count: number): string {
  if (count === 0) return t('measurementDetail.videoCountNone');
  if (count === 1) return t('measurementDetail.videoCountOne');
  return t('measurementDetail.videoCountMany', { count });
}

/**
 * "Download all" — the videos live on a different origin (Azure blob SAS URLs), so the anchor `download`
 * attribute is ignored on them. Each angle is fetched into memory (a same-origin blob: URL honours `download`)
 * and, with more than one, bundled into a single store-only zip: browsers block the 2nd..nth automatic download
 * triggered from one click, which is why only the first video used to save. A fetch that fails (e.g. CORS not
 * configured on the container) falls back to opening that URL in a new tab.
 */
/** Saves a blob through a temporary same-origin object URL (its `download` attribute is honoured). */
function saveBlob(blob: Blob, filename: string): void {
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}

const VideoDownloadAllButton = ({
  videos,
  m,
}: {
  videos: SessionDetailsResponseDto['measurement']['videos'];
  m: MeasurementDetailDto;
}) => {
  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // Fetch every angle in parallel. A failed fetch (e.g. CORS not configured) falls back to opening that URL.
      const fetched = await Promise.all(videos.map(async (v, i) => {
        const base = (v.label ?? `video-${i + 1}`).replace(/[^\w.-]+/g, '_');
        try {
          const response = await fetch(v.url);
          if (!response.ok) throw new Error(String(response.status));
          return { video: v, name: `${base}.mp4`, data: new Uint8Array(await response.arrayBuffer()) };
        } catch {
          return { video: v, name: `${base}.mp4`, data: null };
        }
      }));

      const ok = fetched.filter((f): f is typeof f & { data: Uint8Array } => f.data !== null);
      fetched.filter((f) => f.data === null).forEach((f) => window.open(f.video.url, '_blank', 'noopener'));

      const totalBytes = ok.reduce((sum, f) => sum + f.data.length, 0);
      if (ok.length === 1) {
        saveBlob(new Blob([ok[0].data as BlobPart], { type: 'video/mp4' }), ok[0].name);
      } else if (ok.length > 1 && totalBytes <= MAX_ZIP_BYTES) {
        // One zip = one download: browsers block the 2nd..nth automatic download triggered from a single click.
        const used = new Map<string, number>();
        const entries = ok.map((f) => {
          const n = (used.get(f.name) ?? 0) + 1;
          used.set(f.name, n);
          return { name: n === 1 ? f.name : f.name.replace(/\.mp4$/, `-${n}.mp4`), data: f.data };
        });
        saveBlob(buildZip(entries), `kick-${m.sequenceNumber}-videos.zip`);
      } else {
        // Too large for a plain zip (no ZIP64): the individual videos as separate downloads.
        ok.forEach((f) => saveBlob(new Blob([f.data as BlobPart], { type: 'video/mp4' }), f.name));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type='button'
      className='measurement-detail-download-all'
      onClick={handleClick}
      disabled={busy}
      title={t(videos.length === 1 ? 'measurementDetail.downloadOneTitle' : 'measurementDetail.downloadAllTitle')}
    >
      ⬇ {busy ? t('measurementDetail.downloading') : t(videos.length === 1 ? 'measurementDetail.downloadOne' : 'measurementDetail.downloadAll')}
    </button>
  );
};

/**
 * Kick number per measurement: the backend's per-player kick number when it is distinct across the rows,
 * otherwise a 1-based index in time order (`rows` are time-ordered, earliest first).
 */
function buildKickNumbers(rows: MeasurementDetailDto[]): Map<string, number> {
  const seqNumbers = rows.map((mm) => mm.sequenceNumber);
  const backendNumbersUnique = new Set(seqNumbers).size === seqNumbers.length;
  return new Map(rows.map((mm, index) => [mm.measurementId, backendNumbersUnique ? mm.sequenceNumber : index + 1]));
}

const SessionMeasurementsList = ({
  kicks,
  kickNumbers,
  sessionId,
  currentId,
  sessionType,
}: {
  /** The focused player's kicks, time-ordered (earliest first). */
  kicks: MeasurementDetailDto[];
  kickNumbers: Map<string, number>;
  sessionId: string;
  currentId: string;
  sessionType: string;
}) => {
  const history = useHistory();
  // Newest kick on top.
  const rows = useMemo(() => [...kicks].reverse(), [kicks]);

  if (rows.length === 0) {
    return <p className='chart-empty'>{t('measurementDetail.noOtherMeasurements')}</p>;
  }
  return (
    <ul className='measurement-detail-list'>
      {rows.map((mm) => {
        const isCurrent = mm.measurementId === currentId;
        const href = measurementDetailUrl(sessionId, mm.measurementId, mm.playerId);
        return (
          <li key={mm.measurementId} className={`measurement-detail-list__item${isCurrent ? ' is-current' : ''}`}>
            <a
              href={href}
              onClick={(e) => {
                // Plain left-click swaps the focused kick in place (URL + refetch); let modified
                // clicks (new tab/window) and the current item fall through to default behaviour.
                if (isCurrent || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                e.preventDefault();
                history.push(href);
              }}
            >
              <span className='measurement-detail-list__seq'>#{kickNumbers.get(mm.measurementId)}</span>
              <KickResultChip result={mm.kickResult} kind={sessionType} onTarget={mm.onTarget ?? null} compact />
              <span className='measurement-detail-list__metric'>{formatMetric(mm.ballSpeed, 'km/h')}</span>
            </a>
          </li>
        );
      })}
    </ul>
  );
};

export default MeasurementDetailPage;
